import { useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import theme from '../../../theme';
import { ManualDAReceiptMethod, validateManualDAReceipt } from '../../../utils/manualDAReceipt';

interface ManualDAReceiptProps {
  registrationId: number;
  disabled?: boolean;
  /** Décalage vertical de la pastille, à ajuster selon la marge haute du bouton voisin. */
  top?: number;
  onSuccess: () => void;
  openSnackbar: (message: string, severity: 'success' | 'error') => void;
}

/**
 * « + » de « Soumettre la DA » : enregistrer une DA faite hors plateforme
 * (MiSIV en direct ou site de l'ANTS). Pendant de la modale « Enregistrer un
 * CTVO généré en externe » de SubmitCTVO. À poser dans un parent en
 * `position: relative`.
 */
const ManualDAReceipt: React.FC<ManualDAReceiptProps> = ({
  registrationId,
  disabled,
  top = -6,
  onSuccess,
  openSnackbar,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [method, setMethod] = useState<ManualDAReceiptMethod>('filename');
  const [fileName, setFileName] = useState('');
  const [firebaseUrl, setFirebaseUrl] = useState('');
  const [shouldInvoice, setShouldInvoice] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const value = method === 'filename' ? fileName : firebaseUrl;
  const inputError = value.length > 0 ? validateManualDAReceipt(method, value) : null;

  const reset = () => {
    setIsOpen(false);
    setMethod('filename');
    setFileName('');
    setFirebaseUrl('');
    setShouldInvoice(true);
  };

  const handleSubmit = async () => {
    const error = validateManualDAReceipt(method, value);
    if (error) {
      openSnackbar(error, 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      await makeAuthenticatedApiCall('post', '/api/da/manual-receipt', {
        registrationId,
        excludeFromInvoicing: !shouldInvoice,
        ...(method === 'filename' ? { receiptFileName: value.trim() } : { receiptFirebaseUrl: value.trim() }),
      });
      openSnackbar('DA externe enregistrée avec succès', 'success');
      reset();
      onSuccess();
    } catch (err: any) {
      console.error("Erreur lors de l'enregistrement de la DA externe:", err);
      openSnackbar(err.response?.data?.error || "Erreur lors de l'enregistrement de la DA externe", 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip title="Enregistrer une DA faite en externe (MiSIV ou ANTS)" placement="top">
        <span style={{ position: 'absolute', top, right: -6, zIndex: 1 }}>
          <Button
            sx={{
              minWidth: '24px',
              width: '24px',
              height: '24px',
              padding: 0,
              fontSize: '16px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: theme.palette.primary.main,
              borderRadius: '50%',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'scale(1.1)',
              },
              '&:disabled': {
                backgroundColor: theme.palette.grey[400],
              },
              transition: 'all 0.2s ease',
            }}
            onClick={() => setIsOpen(true)}
            disabled={disabled}
          >
            +
          </Button>
        </span>
      </Tooltip>

      <Dialog open={isOpen} onClose={() => !isSubmitting && reset()} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Enregistrer une DA faite en externe
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            La DA passera en traité et l'agence recevra le mail de récépissé.
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Cette DA doit-elle être intégrée dans une facture ?
            </Typography>
            <RadioGroup
              value={shouldInvoice ? 'yes' : 'no'}
              onChange={(e) => setShouldInvoice(e.target.value === 'yes')}
            >
              <FormControlLabel
                className="inputElement"
                value="yes"
                control={<Radio />}
                label="Oui, intégrer dans la facturation"
                disabled={isSubmitting}
              />
              <FormControlLabel
                className="inputElement"
                value="no"
                control={<Radio />}
                label="Non, ne pas facturer (déjà facturé manuellement)"
                disabled={isSubmitting}
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              Comment retrouver le récépissé ?
            </Typography>
            <RadioGroup row value={method} onChange={(e) => setMethod(e.target.value as ManualDAReceiptMethod)}>
              <FormControlLabel value="filename" control={<Radio />} label="Nom du fichier" disabled={isSubmitting} />
              <FormControlLabel value="url" control={<Radio />} label="URL Firebase" disabled={isSubmitting} />
            </RadioGroup>
          </FormControl>

          {method === 'filename' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                DA faite sur MiSIV : entrez le nom du fichier du récépissé. Le système le télécharge depuis le ministère
                et l'enregistre sur Firebase.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                className="inputElement"
                label="Nom du fichier DA"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="Ex: DA-REC_GA-036-HE_2026-09-24-174047.pdf"
                disabled={isSubmitting}
                helperText={inputError ?? "Le nom doit commencer par 'DA-'"}
                error={!!inputError}
                margin="dense"
              />
            </Box>
          )}

          {method === 'url' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
                DA faite sur le site de l'ANTS : collez l'URL Firebase du récépissé PDF déjà uploadé.
              </Typography>
              <TextField
                autoFocus
                fullWidth
                className="inputElement"
                label="URL Firebase du fichier PDF"
                value={firebaseUrl}
                onChange={(e) => setFirebaseUrl(e.target.value)}
                placeholder="https://firebasestorage.googleapis.com/..."
                disabled={isSubmitting}
                helperText={inputError ?? "L'URL doit être une URL Firebase et pointer vers un fichier PDF"}
                error={!!inputError}
                margin="dense"
                multiline
                rows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={reset} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button
            className="dealActionButton"
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || !value.trim()}
          >
            {isSubmitting && <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />}
            Valider
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManualDAReceipt;
